/**
 * Canonical license categories, mirroring
 * `workd_mission_control.lm_license_category_normalization.canonical_category`.
 *
 * Unlike `LicenseSourceType` (which is a tight enum on `client_license.type`),
 * `license_category` lives on `lm_registry` only and is varchar(50). The
 * 26 values below are the ones the normalization table currently emits.
 * Adding a new canonical category? Add it here AND label it below.
 *
 * Display goes through `getLicenseCategoryLabel()` — never render the raw
 * SCREAMING_SNAKE_CASE value in the UI.
 */
export const LICENSE_CATEGORIES = [
	'PHARMACY_FACILITY',
	'PHARMACY_FACILITY_OUT_OF_STATE',
	'PHARMACY_REMOTE',
	'OUTSOURCING_FACILITY',
	'VETERINARY_FACILITY',
	'PHARMACIST',
	'PHARMACIST_INTERN',
	'PHARMACY_TECHNICIAN',
	'PHARMACY_TECHNICIAN_TRAINEE',
	'VETERINARIAN',
	'VETERINARY_TECHNICIAN',
	'PHYSICIAN',
	'PHYSICIAN_ASSISTANT',
	'NURSE_PRACTITIONER',
	'REGISTERED_NURSE',
	'LICENSED_PRACTICAL_NURSE',
	'AESTHETICIAN',
	'WHOLESALE_DISTRIBUTOR',
	'DRUG_DISTRIBUTOR',
	'THIRD_PARTY_LOGISTICS',
	'MANUFACTURER',
	'DRUG_REGISTRATION',
	'CONTROLLED_SUBSTANCE_REGISTRATION',
	'CONTROLLED_SUBSTANCE_LAB',
	'CONTROLLED_SUBSTANCE_VETERINARY',
	'CONTROLLED_SUBSTANCE_AUTOMATED_DEVICE',
	'OTHER',
] as const;

export type LicenseCategory = (typeof LICENSE_CATEGORIES)[number];

/**
 * Human-readable display labels. Title-cased, no underscores. Used by
 * the licenses list "License #" sub-line, the LicenseReviewModal type
 * groupings, the registry data card, and anywhere else we render the
 * category to users.
 */
export const LICENSE_CATEGORY_LABEL: Readonly<Record<LicenseCategory, string>> =
	{
		PHARMACY_FACILITY: 'Pharmacy Facility',
		PHARMACY_FACILITY_OUT_OF_STATE: 'Pharmacy Facility (Out of State)',
		PHARMACY_REMOTE: 'Remote Pharmacy',
		OUTSOURCING_FACILITY: 'Outsourcing Facility',
		VETERINARY_FACILITY: 'Veterinary Facility',
		PHARMACIST: 'Pharmacist',
		PHARMACIST_INTERN: 'Pharmacist Intern',
		PHARMACY_TECHNICIAN: 'Pharmacy Technician',
		PHARMACY_TECHNICIAN_TRAINEE: 'Pharmacy Technician Trainee',
		VETERINARIAN: 'Veterinarian',
		VETERINARY_TECHNICIAN: 'Veterinary Technician',
		PHYSICIAN: 'Physician',
		PHYSICIAN_ASSISTANT: 'Physician Assistant',
		NURSE_PRACTITIONER: 'Nurse Practitioner',
		REGISTERED_NURSE: 'Registered Nurse',
		LICENSED_PRACTICAL_NURSE: 'Licensed Practical Nurse',
		AESTHETICIAN: 'Aesthetician',
		WHOLESALE_DISTRIBUTOR: 'Wholesale Distributor',
		DRUG_DISTRIBUTOR: 'Drug Distributor',
		THIRD_PARTY_LOGISTICS: 'Third-Party Logistics',
		MANUFACTURER: 'Manufacturer',
		DRUG_REGISTRATION: 'Drug Registration',
		CONTROLLED_SUBSTANCE_REGISTRATION: 'Controlled Substance Registration',
		CONTROLLED_SUBSTANCE_LAB: 'Controlled Substance Lab',
		CONTROLLED_SUBSTANCE_VETERINARY: 'Controlled Substance (Veterinary)',
		CONTROLLED_SUBSTANCE_AUTOMATED_DEVICE:
			'Controlled Substance (Automated Device)',
		OTHER: 'Other',
	} as const;

/**
 * PD-14842 — coarse category GROUPS for the matchmaking category toggles. A
 * tenant disables a whole group (e.g. "Veterinary") rather than ticking each
 * underlying `license_category`. Each group maps to the canonical categories it
 * covers; disabling a group excludes all of its member categories from
 * matchmaking (see the matchmaker slice filter). Add a key here and its member
 * categories below to expose a new toggle — the config-app checkbox list and
 * the enforcement both read from these two exports.
 */
export const LICENSE_CATEGORY_GROUP_KEYS = ['VETERINARY'] as const;

export type LicenseCategoryGroup = (typeof LICENSE_CATEGORY_GROUP_KEYS)[number];

export const LICENSE_CATEGORY_GROUPS: Readonly<
	Record<LicenseCategoryGroup, ReadonlyArray<LicenseCategory>>
> = {
	VETERINARY: [
		'VETERINARY_FACILITY',
		'VETERINARIAN',
		'VETERINARY_TECHNICIAN',
		'CONTROLLED_SUBSTANCE_VETERINARY',
	],
} as const;

/**
 * Domain-aware sort rank. The LicenseReviewModal groups suggestions by
 * address and then sorts within each group by category rank — facilities
 * anchor the group (they're the parent license), then individuals
 * (pharmacists, techs), then distributors / manufacturers, then
 * controlled-substance registrations, then misc.
 *
 * Categories not in this map sort to the end (rank 999) but are still
 * grouped together by name.
 */
export const LICENSE_CATEGORY_SORT_RANK: Readonly<
	Record<LicenseCategory, number>
> = {
	// Facilities (anchor the group)
	PHARMACY_FACILITY: 10,
	PHARMACY_FACILITY_OUT_OF_STATE: 11,
	PHARMACY_REMOTE: 12,
	OUTSOURCING_FACILITY: 13,
	VETERINARY_FACILITY: 14,
	// Individual practitioners working at facilities
	PHARMACIST: 20,
	PHARMACIST_INTERN: 21,
	PHARMACY_TECHNICIAN: 22,
	PHARMACY_TECHNICIAN_TRAINEE: 23,
	VETERINARIAN: 24,
	VETERINARY_TECHNICIAN: 25,
	PHYSICIAN: 26,
	PHYSICIAN_ASSISTANT: 27,
	NURSE_PRACTITIONER: 28,
	REGISTERED_NURSE: 29,
	LICENSED_PRACTICAL_NURSE: 30,
	AESTHETICIAN: 31,
	// Supply-chain entities
	WHOLESALE_DISTRIBUTOR: 40,
	DRUG_DISTRIBUTOR: 41,
	THIRD_PARTY_LOGISTICS: 42,
	MANUFACTURER: 43,
	// State drug/device permits (facility-level drug-handling authorization)
	DRUG_REGISTRATION: 44,
	// Controlled-substance registrations (separate regulatory track)
	CONTROLLED_SUBSTANCE_REGISTRATION: 50,
	CONTROLLED_SUBSTANCE_LAB: 51,
	CONTROLLED_SUBSTANCE_VETERINARY: 52,
	CONTROLLED_SUBSTANCE_AUTOMATED_DEVICE: 53,
	// Fallback
	OTHER: 99,
};

/**
 * Convert SCREAMING_SNAKE_CASE (or kebab-case, or "lower case with spaces")
 * to Title Case. Used as a graceful fallback when a category value comes
 * through that isn't in `LICENSE_CATEGORY_LABEL` — typically a raw
 * `license_type` string that bypassed normalization, or a freshly-added
 * canonical that hasn't been labeled yet. Better to render
 * "Pharmacy Drop Shipper" than yell PHARMACY_DROP_SHIPPER at the user.
 */
function toTitleCase(raw: string): string {
	return raw
		.split(/[_\s-]+/)
		.filter((part) => part.length > 0)
		.map((part) => {
			const lower = part.toLowerCase();
			return lower.charAt(0).toUpperCase() + lower.slice(1);
		})
		.join(' ');
}

/**
 * Resolve any category value (canonical or raw) to its display label.
 * Returns the empty string for null/undefined/empty so callers can chain
 * `?? ''` or fall back to a placeholder.
 */
export function getLicenseCategoryLabel(
	category: string | null | undefined,
): string {
	if (category == null) {
		return '';
	}
	const trimmed = category.trim();
	if (trimmed === '') {
		return '';
	}
	const upper = trimmed.toUpperCase();
	if (upper in LICENSE_CATEGORY_LABEL) {
		return LICENSE_CATEGORY_LABEL[upper as LicenseCategory];
	}
	return toTitleCase(trimmed);
}

/**
 * Sort-rank for a category. Unknown categories (or null) get 999 so they
 * land at the end of any sort using this rank.
 */
export function getLicenseCategorySortRank(
	category: string | null | undefined,
): number {
	if (category == null) {
		return 999;
	}
	const upper = category.trim().toUpperCase();
	if (upper in LICENSE_CATEGORY_SORT_RANK) {
		return LICENSE_CATEGORY_SORT_RANK[upper as LicenseCategory];
	}
	return 999;
}
